/** 拒收台账（rejected.md）：按环节分组（桥接翻译 / 运行时合约校验 / 常驻投递），原因原样显示，通报列缺即标「缺」。
 *
 * 主口径 `GET /signals/rejections`（服务端汇总既有产物，不重新校验信号）；桥接条目的键已由后端并成 `ref`（裁决 B9）。
 */

import { useState } from "react";

import { useRejections, type Rejection } from "../../api/g4";
import { Card, Kpi, Lede, Note, PageHead } from "../../components/Card";
import { Table } from "../../components/Table";
import { Missing, Section } from "../g4/Section";

const STAGES: { stage: Rejection["stage"]; title: string; sub: string }[] = [
  { stage: "bridge", title: "桥接翻译拒收", sub: "收件文件 rejected[]（翻不出来的走拒收）" },
  { stage: "run", title: "运行时合约校验拒收", sub: "var/paper/<sid>/<date>/run.json signals.rejected[]" },
  { stage: "deliver", title: "常驻投递拒收", sub: "GET /signals?status=rejected（只统计 POST /signals/deliver）" },
];

export function RejectedScreen(): React.ReactNode {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [line, setLine] = useState("");
  const params = { from: from || undefined, to: to || undefined, line: line || undefined };
  const query = useRejections(params);

  return (
    <>
      <PageHead title="拒收台账" sub="GET /signals/rejections" />
      <Lede>不合规信号不许静默丢弃：哪些被拒、在哪一环被拒、原因原文、有没有通报来源。页头口径：当前合约 v2；v1 可读截止 2026-12-24。</Lede>
      <Card title="筛选">
        <div className="fieldrow">
          <label className="f"><span>from</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="f"><span>to</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
          <label className="f">
            <span>信号线</span>
            <select value={line} onChange={(e) => setLine(e.target.value)}>
              <option value="">全部</option>
              <option value="someopark">someopark</option>
              <option value="someopark-test">someopark-test</option>
            </select>
          </label>
        </div>
      </Card>
      <Section query={query} what="拒收汇总" path="/signals/rejections">
        {(data) => {
          const total = data.counts.bridge + data.counts.run + data.counts.deliver;
          return (
            <>
              <div className="grid g3">
                <Kpi label="桥接翻译" value={data.counts.bridge} />
                <Kpi label="运行时合约校验" value={data.counts.run} />
                <Kpi label="常驻投递" value={data.counts.deliver} />
              </div>
              {total === 0 ? (
                <Note>所选区间（{from || "不限"} ~ {to || "不限"}，{line || "全部信号线"}）三个环节都没有拒收信号</Note>
              ) : null}
              {data.rejections.some((row) => row.notified === null) ? (
                <Note tone="warn">通报动作尚未实现（渠道待裁决），通报列整列标「缺」，拒收只落台账。</Note>
              ) : null}
              {STAGES.map(({ stage, title, sub }) => (
                <Card key={stage} title={title} sub={sub} nopad>
                  <Table
                    cols={[{ label: "信号线" }, { label: "策略", mono: true }, { label: "交易日" }, { label: "ref", mono: true }, { label: "原因", wrap: true }, { label: "证据", mono: true, wrap: true }, { label: "通报" }]}
                    rows={data.rejections
                      .filter((row) => row.stage === stage)
                      .map((row, i) => ({
                        key: `${stage}-${i}`,
                        cls: "bad",
                        cells: [
                          row.source_line ?? "—",
                          row.strategy_id ?? "—",
                          row.trade_date ?? "—",
                          row.ref ?? <Missing />,
                          row.reason ?? <Missing />,
                          row.evidence,
                          row.notified === null ? <Missing /> : row.notified ? `已通报 ${row.notified_via ?? ""}` : "未通报",
                        ],
                      }))}
                    empty={stage === "deliver" ? "常驻台账没有拒收（只统计 POST /signals/deliver）" : "该环节在所选区间没有拒收"}
                  />
                </Card>
              ))}
            </>
          );
        }}
      </Section>
    </>
  );
}
